import DeleteIcon from '@mui/icons-material/Delete';
import { TabContext, TabList, TabPanel } from '@mui/lab';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  Grid,
  IconButton,
  LinearProgress,
  Tab,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import { isEqual } from 'lodash';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { NATURAL_PERSON_CLIENT_TYPE_ID } from '../constants';
import { useAlert } from '../context/AlertContext';
import { showConfirmDialogComponent } from '../core/ConfirmDialog';
import { LoadingDialog } from '../core/LoadingDialog';
import { NoDataDialog } from '../core/NoDataDialog';
import {
  useCreateLegalClientFullObject,
  useDeleteLegalClient,
  useGetLegalClientContactInformationTypes,
  useGetLegalClientFullObject,
  useGetLegalClients,
  useGetLegalClientTypes,
  useUpdateLegalClientFullObject,
} from '../hooks/reactQuerys/LegalClientQueries';
import {
  useCreateRelationshipFullObject,
  useDeleteRelationship,
  useGetRelationshipsByLegalClientId,
  useUpdateRelationshipFullObject,
} from '../hooks/reactQuerys/RelationshipQueries';
import {
  ILegalClientFullObject,
  initLegalClient,
  legalClientFullObjectSchema,
  transferLegalClientInput,
} from '../Interfaces/ILegalClient';
import { ILegalClientRelationship } from '../Interfaces/ILegalClientRelationship/ILegalClientRelationship';
import { legalClientRelationshipInputSchema } from '../Interfaces/ILegalClientRelationship/ILegalClientRelationshipInput';
import { ILegalTransaction } from '../Interfaces/ILegalTransaction';
import { AddressTab } from './AddressTab/AddressTab';
import { AddArnotopSyncDialog } from './Arnotop/AddArnotopSyncDialog';
import { BankInformationTab } from './BankInformationTab/BankInformationTab';
import { ContactInformationTab } from './ContactInformationTab/ContactInformationTab';
import { LegalClientTypeForm } from './LegalClientTypeForm';
import { LegalClientUserForm } from './LegalClientUserForm';
import { LegalPersonForm } from './LegalPersonForm';
import { LegalTransactionsTab } from './LegalTransactionsTab/LegalTransactionsTab';
import { LogTab } from './LogTab/LogTab';
import { NaturalPersonForm } from './NaturalPersonForm';
import { RelationshipsTab } from './RelationsTab/RelationshipTab';

interface IProps {
  editedLegalClientId?: number;
  unmount?: (editedLegalClient?: ILegalClientFullObject) => void;
  saveMethod?: SaveMethod;
}

type SaveMethod = 'save' | 'saveAndClose';

export const EditLegalClientDialog = ({
  editedLegalClientId,
  unmount,
  saveMethod = 'save',
}: IProps) => {
  const formRef = useRef<HTMLFormElement>(null);
  const navigate = useNavigate();
  const theme = useTheme();
  const [formValues, setFormValues] =
    useState<ILegalClientFullObject>(initLegalClient());
  const [savedFormValues, setSavedFormValues] = useState(initLegalClient());
  const [relationships, setRelationships] = useState<
    ILegalClientRelationship[]
  >([]);
  const [savedRelationships, setSavedRelationships] = useState<
    ILegalClientRelationship[]
  >([]);

  const [selectedLegalTransactions, setSelectedLegalTransactions] = useState<
    ILegalTransaction[]
  >([]);

  const [isOpenArnoTopSync, setIsOpenArnoTopSync] = useState(false);
  const [isProcessingUpdate, setIsProcessingUpdate] = useState(false);

  const [currentTab, setCurrentTab] = useState('1');

  const { legalClientId } = useParams();

  const memorizedEditedLegalClientId = useMemo(() => {
    return editedLegalClientId ? editedLegalClientId : Number(legalClientId);
  }, [editedLegalClientId, legalClientId]);

  const { showAlert } = useAlert();

  const { legalClients, isLoadingLegalClients, refetchLegalClients } =
    useGetLegalClients();

  const {
    contactInformationTypes,
    isLoadingContactInformationTypes,
    refetchContactInformationTypes,
  } = useGetLegalClientContactInformationTypes();

  const {
    legalClientTypes,
    isLoadingLegalClientTypes,
    refetchLegalClientTypes,
  } = useGetLegalClientTypes();

  const {
    createRelationshipFullObject,
    isPendingCreateRelationshipFullObject,
  } = useCreateRelationshipFullObject();

  const { createLegalClientFullObject, isPendingCreateLegalClientFullObject } =
    useCreateLegalClientFullObject();

  const { deleteLegalClient, isPendingDeleteLegalClient } =
    useDeleteLegalClient();

  const {
    legalClientRelationships,
    isLoadingLegalClientRelationships,
    refetchLegalClientRelationships,
  } = useGetRelationshipsByLegalClientId(memorizedEditedLegalClientId);

  const {
    legalClientFullObject,
    isFetchingLegalClientFullObject,
    refetchLegalClientFullObject,
  } = useGetLegalClientFullObject(memorizedEditedLegalClientId);

  const { updateLegalClientFullObject, isPendingUpdateLegalClientFullObject } =
    useUpdateLegalClientFullObject();

  const {
    updateRelationshipFullObject,
    isPendingUpdateRelationshipFullObject,
  } = useUpdateRelationshipFullObject();

  const { deleteRelationship, isPendingDeleteRelationship } =
    useDeleteRelationship();

  useEffect(() => {
    if (
      !legalClientRelationships ||
      isEqual(legalClientRelationships, relationships)
    )
      return;
    setRelationships(legalClientRelationships);
    setSavedRelationships(legalClientRelationships);
  }, [legalClientRelationships]);

  useEffect(() => {
    if (!legalClientFullObject || isEqual(formValues, legalClientFullObject))
      return;
    setFormValues(legalClientFullObject!);
    setSavedFormValues(legalClientFullObject!);
    setSelectedLegalTransactions(
      legalClientFullObject.LegalTransactionArray.map(lt => lt.LegalTransaction)
    );
  }, [legalClientFullObject]);

  useEffect(() => {
    if (!selectedLegalTransactions) return;
    setFormValues(prev => ({
      ...prev,
      LegalTransactionArray: selectedLegalTransactions.map(lt => ({
        LegalTransaction: lt,
        LegalRole: null,
        idLegalRole: null,
      })),
    }));
  }, [selectedLegalTransactions]);

  const resetForm = () => {
    setFormValues(legalClientFullObject!);
    setSavedFormValues(legalClientFullObject!);
    setRelationships(legalClientRelationships!);
    setSavedRelationships(legalClientRelationships!);
  };

  const onUpdate = async (legalClient: ILegalClientFullObject = formValues) => {
    try {
      setIsProcessingUpdate(true);
      const input: ILegalClientFullObject =
        transferLegalClientInput(legalClient);

      const { success, data, error } =
        legalClientFullObjectSchema.safeParse(input);

      if (
        !success ||
        (formValues.idLegalClientType === 1 && !formValues.FirstName) // Manuel validation of FirstName because it is not required in the schema but should be required in the form
      ) {
        console.error(error);
        showAlert({
          text: 'Bitte füllen Sie alle Felder aus',
          severity: 'error',
        });
        setIsProcessingUpdate(false);
        return;
      }

      if (isMainFormEdited) {
        if (legalClient.idLegalClient < 0) {
          const updatedLegalClient = await createLegalClientFullObject(data);
          setSavedFormValues(updatedLegalClient);
          setFormValues(updatedLegalClient);
          await refetchLegalClients();
        } else {
          const updatedLegalClient = await updateLegalClientFullObject(data);
          setSavedFormValues(updatedLegalClient);
          setFormValues(updatedLegalClient);
          await refetchLegalClients();
        }
      }

      if (isRelationshipsFormEdited) {
        await handleRelationships();
        await refetchAll();
      }

      showAlert({ text: 'Mandant erfolgreich bearbeitet' });
      setIsProcessingUpdate(false);
      if (saveMethod === 'saveAndClose') closeMethod(formValues);
    } catch (error) {
      await refetchAll();

      showAlert({
        text: 'Fehler beim Speichern des Mandanten',
        severity: 'error',
      });
      resetForm();

      setIsProcessingUpdate(false);
    }
  };

  const handleRelationships = async () => {
    let tasks: Promise<void>[] = [];

    // If no existing relationships, create all from `relationships`
    if (!legalClientRelationships) {
      tasks = relationships.map(r =>
        createRelationshipFullObject(
          legalClientRelationshipInputSchema.parse(r)
        )
      );
    } else {
      // Create a map for quick lookups of existing relationships
      const existingMap = new Map(
        legalClientRelationships.map(rel => [rel.idRelationship, rel])
      );

      for (const r of relationships) {
        if (r.idRelationship < 0) {
          // Create new relationship
          tasks.push(
            createRelationshipFullObject(
              legalClientRelationshipInputSchema.parse(r)
            )
          );
        } else {
          const existing = existingMap.get(r.idRelationship);
          if (existing) {
            if (!isEqual(existing, r)) {
              // Update only if there's a change
              tasks.push(
                updateRelationshipFullObject(
                  legalClientRelationshipInputSchema.parse(r)
                )
              );
            }
            existingMap.delete(r.idRelationship);
          }
        }
      }

      tasks.push(
        ...Array.from(existingMap.values()).map(({ idRelationship }) =>
          deleteRelationship(idRelationship)
        )
      );
    }
    await Promise.all(tasks);
  };

  const onSubmit = () => {
    if (formRef.current && formRef.current.checkValidity()) {
      onUpdate();
    } else if (formRef.current && !formRef.current.checkValidity()) {
      const elements = Array.from(formRef.current.elements).reverse();

      elements.forEach((element: any) => {
        if (element.reportValidity()) {
          element.reportValidity();
        }
      });
    }
  };

  const closeMethod = (editedLegalClient?: ILegalClientFullObject) =>
    unmount ? unmount(editedLegalClient) : navigate('/clients');

  const handleConfirmedClose = async () => {
    if (isProcessingUpdate) return;
    if (isEdited) {
      const isConfirm = await showConfirmDialogComponent(
        'Warnung',
        'Sind Sie sicher, dass Sie den Mandanten ohne Speichern schließen wollen?',
        'Schließen',
        'Abbrechen',
        theme
      );
      if (isConfirm) closeMethod();
    } else {
      closeMethod();
    }
  };

  const refetchAll = () => {
    refetchLegalClients();
    refetchContactInformationTypes();
    refetchLegalClientTypes();
    refetchLegalClientRelationships();
    refetchLegalClientFullObject();
  };

  const isDuplicateEmail =
    formValues.Email &&
    formValues.Email !== savedFormValues.Email &&
    legalClients?.some(
      lc =>
        lc.idLegalClient !== memorizedEditedLegalClientId &&
        lc.Email === formValues.Email
    );

  const isMainFormEdited = !isEqual(formValues, savedFormValues);
  const isRelationshipsFormEdited = !isEqual(relationships, savedRelationships);

  const isEdited = isMainFormEdited || isRelationshipsFormEdited;

  const onDelete = useCallback(async () => {
    const isConfirm = await showConfirmDialogComponent(
      'Warnung',
      'Sind Sie sicher, dass Sie diesen Mandanten löschen wollen?',
      'Löschen',
      'Schließen',
      theme
    );
    if (!isConfirm) return;
    await deleteLegalClient(formValues.idLegalClient);
    closeMethod();
  }, [formValues.idLegalClient]);

  // This is needed to track first and last name changes in the relationships tab
  const liveUpdatedRelationships = useMemo(
    () =>
      relationships.map(r => ({
        ...r,
        Members: r.Members.map(m =>
          m.idLegalClient === memorizedEditedLegalClientId
            ? {
                ...m,
                LegalClient: {
                  ...m.LegalClient,
                  FirstName: formValues.FirstName,
                  LastName: formValues.LastName,
                },
              }
            : m
        ),
      })),
    [relationships, formValues.FirstName, formValues.LastName]
  );

  const isPending =
    isPendingUpdateLegalClientFullObject ||
    isPendingUpdateRelationshipFullObject ||
    isPendingDeleteRelationship ||
    isPendingCreateRelationshipFullObject ||
    isPendingCreateLegalClientFullObject;

  if (
    isLoadingLegalClients ||
    isLoadingContactInformationTypes ||
    isLoadingLegalClientTypes ||
    isLoadingLegalClientRelationships ||
    isFetchingLegalClientFullObject ||
    isPendingDeleteLegalClient
  )
    return <LoadingDialog unmount={closeMethod} />;

  if (
    legalClientTypes === undefined ||
    legalClients === undefined ||
    (editedLegalClientId !== undefined &&
      editedLegalClientId > 0 &&
      (contactInformationTypes === undefined ||
        legalClientRelationships === undefined ||
        legalClientFullObject === undefined))
  )
    return <NoDataDialog unmount={closeMethod} refetch={refetchAll} />;

  return (
    <>
      <Dialog
        open
        onClose={handleConfirmedClose}
        maxWidth='lg'
        fullWidth
        scroll='paper'
      >
        {isPending && (
          <LinearProgress
            sx={{
              height: '4px',
            }}
          />
        )}
        <Box
          sx={{
            borderBottom: 1,
            borderColor: 'divider',
            px: 3,
            py: 2,
          }}
        >
          <Box
            display='flex'
            justifyContent='space-between'
            alignItems='center'
            width='100%'
          >
            <Typography variant='h5'>
              {formValues?.FirstName} {formValues?.LastName}
            </Typography>
            <IconButton onClick={onDelete}>
              <DeleteIcon />
            </IconButton>
          </Box>
        </Box>

        <DialogContent sx={{ minHeight: '70vh' }}>
          {isDuplicateEmail && (
            <Alert severity='warning'>
              <AlertTitle>Warnung: E-Mail-Adresse</AlertTitle>
              Die ausgewählte E-Mail-Adresse existiert bereits im System und
              kann nicht verwendet werden!
            </Alert>
          )}
          <Grid container spacing={2}>
            <form ref={formRef}>
              <Grid container item spacing={2} pt={1}>
                <Grid item xs={3}>
                  <LegalClientTypeForm
                    formValues={formValues}
                    setFormValues={setFormValues}
                    legalClientTypes={legalClientTypes!}
                  />
                </Grid>

                {formValues.idLegalClientType ===
                NATURAL_PERSON_CLIENT_TYPE_ID ? (
                  <NaturalPersonForm
                    formValues={formValues}
                    setFormValues={setFormValues}
                  />
                ) : (
                  <LegalPersonForm
                    formValues={formValues}
                    setFormValues={setFormValues}
                  />
                )}
              </Grid>
            </form>

            <Grid item xs={12}>
              <LegalClientUserForm
                formValues={formValues}
                setFormValues={setFormValues}
              />
            </Grid>

            {/*
            <Grid item xs={12}>
              <ArnoTopSyncForm
                legalClient={formValues}
                onCheckedChange={checked => {
                  if (checked) {
                    setFormValues({
                      ...formValues,
                      syncWithArnoTop: true,
                      syncType: 1,
                    });
                    setIsOpenArnoTopSync(true);
                  } else {
                    setFormValues({
                      ...formValues,
                      syncWithArnoTop: false,
                      syncType: undefined,
                      idArnoTop: undefined,
                    });
                  }
                }}
              />
            </Grid>
             */}
          </Grid>

          <TabContext value={currentTab}>
            <Box sx={{ borderBottom: 1, borderColor: 'divider', mt: 4 }}>
              <TabList onChange={(_, newValue) => setCurrentTab(newValue)}>
                <Tab label='Addressen' value='1' />
                <Tab label='Kontakt' value='2' />
                <Tab label='Rechtsgeschäfte' value='3' />
                <Tab label='Beziehungen' value='4' />
                <Tab label='Bankinformationen' value='5' />
                <Tooltip title='Noch nicht verfügbar'>
                  <Box>
                    <Tab label='Gesprächsprotokolle' value='6' disabled />
                  </Box>
                </Tooltip>
                <Tooltip title='Noch nicht verfügbar'>
                  <Box>
                    <Tab label='Dokumente' value='7' disabled />
                  </Box>
                </Tooltip>
                <Tab label='Log' value='8' />
              </TabList>
            </Box>

            <TabPanel value='1'>
              <AddressTab
                formValues={formValues}
                setFormValues={setFormValues}
              />
            </TabPanel>
            <TabPanel value='2'>
              <ContactInformationTab
                contactInformationTypes={contactInformationTypes!}
                formValues={formValues}
                setFormValues={setFormValues}
              />
            </TabPanel>
            <TabPanel value='3'>
              <LegalTransactionsTab
                legalTransactions={selectedLegalTransactions}
                setLegalTransactions={setSelectedLegalTransactions}
              />
            </TabPanel>
            <TabPanel value='4'>
              <RelationshipsTab
                editedLegalClientId={memorizedEditedLegalClientId}
                legalClients={legalClients!}
                relationships={liveUpdatedRelationships}
                setRelationships={setRelationships}
              />
            </TabPanel>
            <TabPanel value='5'>
              <BankInformationTab
                formValues={formValues}
                setFormValues={setFormValues}
              />
            </TabPanel>

            <TabPanel value='8'>
              <LogTab idLegalClient={memorizedEditedLegalClientId} />
            </TabPanel>
          </TabContext>
        </DialogContent>
        <DialogActions
          sx={{ px: 3, py: 2, borderTop: 1, borderColor: 'divider' }}
        >
          <Button variant='outlined' onClick={handleConfirmedClose}>
            Schließen
          </Button>
          <Button
            variant='contained'
            disabled={
              isPendingUpdateLegalClientFullObject ||
              isDuplicateEmail ||
              isProcessingUpdate
            }
            onClick={onSubmit}
          >
            {isPending || isProcessingUpdate ? (
              <Box display='flex' alignItems='center' gap={1}>
                <span>Speichern</span>
                <CircularProgress size={20} />
              </Box>
            ) : (
              'Speichern'
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {isOpenArnoTopSync && (
        <AddArnotopSyncDialog
          legalClient={formValues}
          onSave={arnotopSyncState => {
            setFormValues({
              ...formValues,
              syncWithArnoTop: arnotopSyncState.syncWithArnoTop,
              syncType: arnotopSyncState.syncType,
              idArnoTop: arnotopSyncState.idArnoTop,
            });
            setIsOpenArnoTopSync(false);
          }}
        />
      )}
    </>
  );
};
